import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { strongSelectMenuProps } from "components/common/dropdownPaper";
import {
  blurActiveElement,
  FILTER_FIELD_H,
  FILTER_FIELD_W,
} from "./DateRangeSelect";

export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Single-select filter cell matching the date-range cells: a floating label
 * that only lifts when a value is set (shrink/notched keyed to the value), so
 * it never truncates or hangs after the menu is dismissed. An empty value maps
 * to the ``allLabel`` ("Any") option.
 */
export default function FilterSelect({
  label,
  value,
  options,
  onChange,
  allLabel = "Any",
  width = FILTER_FIELD_W,
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  allLabel?: string;
  width?: number;
}) {
  const hasValue = Boolean(value);
  const labelId = `filter-${label.replace(/\s+/g, "-").toLowerCase()}`;
  const renderValue = (v: string) =>
    options.find((o) => o.value === v)?.label ?? "";

  // NOTE: the shrunk label overhangs ~9px above the FormControl top, so the
  // surrounding container must leave that much headroom (no flush
  // overflow-hidden ancestors right above the filter row).
  return (
    <FormControl size="small" sx={{ width }}>
      <InputLabel id={labelId} shrink={hasValue}>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        label={label}
        notched={hasValue}
        value={value}
        renderValue={renderValue}
        onChange={(e: SelectChangeEvent) => onChange(e.target.value)}
        onClose={blurActiveElement}
        sx={{ height: FILTER_FIELD_H }}
        MenuProps={strongSelectMenuProps}
      >
        <MenuItem value="">{allLabel}</MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
